/**
 * Curated Instagram Row - JavaScript
 * Handles modal opening, lazy-loading Instagram embeds, and accessibility
 */

(function() {
  'use strict';

  // State
  let embedScriptLoaded = false;
  let embedScriptLoading = false;
  let activeModal = null;
  let triggerElement = null;

  // Init all Instagram row sections
  function init() {
    const sections = document.querySelectorAll('.jit-ig-row');
    sections.forEach(initSection);
  }

  function initSection(section) {
    const track = section.querySelector('.jit-ig-row__track');
    const prevBtn = section.querySelector('.jit-ig-row__nav--prev');
    const nextBtn = section.querySelector('.jit-ig-row__nav--next');
    const modal = section.querySelector('.jit-ig-modal');
    const tileBtns = section.querySelectorAll('.jit-ig-row__tile-btn');
    const closeElements = section.querySelectorAll('[data-ig-close]');

    // Scroll navigation
    if (prevBtn && nextBtn && track) {
      // Scroll by ~one tile so navigation feels consistent across breakpoints.
      const sectionStyles = window.getComputedStyle(section);
      const trackStyles = window.getComputedStyle(track);
      const tileSize = parseFloat(sectionStyles.getPropertyValue('--tile-size')) || 180;
      const gap = parseFloat(trackStyles.getPropertyValue('gap')) || 16;
      const scrollAmount = Math.round(tileSize + gap);
      
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
      
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });

      // Update nav visibility based on scroll position
      function updateNavVisibility() {
        const { scrollLeft, scrollWidth, clientWidth } = track;
        prevBtn.style.opacity = scrollLeft > 10 ? '1' : '0.3';
        nextBtn.style.opacity = scrollLeft < scrollWidth - clientWidth - 10 ? '1' : '0.3';
      }

      track.addEventListener('scroll', updateNavVisibility);
      updateNavVisibility();
    }

    // Tile click handlers
    tileBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.igUrl;
        if (url && modal) {
          openModal(modal, url, btn);
        }
      });
    });

    // Close handlers
    closeElements.forEach(el => {
      el.addEventListener('click', () => closeModal(modal));
    });

    // Keyboard close
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeModal(modal);
      }
    });
  }

  function openModal(modal, postUrl, trigger) {
    activeModal = modal;
    triggerElement = trigger;

    const embedContainer = modal.querySelector('.jit-ig-modal__embed');
    const loadingEl = modal.querySelector('.jit-ig-modal__loading');
    const fallbackEl = modal.querySelector('.jit-ig-modal__fallback');
    const fallbackLink = modal.querySelector('.jit-ig-modal__fallback-link');
    const captionEl = modal.querySelector('.jit-ig-modal__caption');

    // Reset state
    embedContainer.innerHTML = '';
    loadingEl.hidden = false;
    fallbackEl.hidden = true;
    fallbackLink.href = postUrl;
    if (captionEl) {
      const caption = trigger?.dataset?.igCaption || '';
      const cleaned = caption.trim();
      if (cleaned) {
        captionEl.textContent = cleaned;
        captionEl.hidden = false;
      } else {
        captionEl.textContent = '';
        captionEl.hidden = true;
      }
    }

    // Show modal
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    // Focus the close button
    const closeBtn = modal.querySelector('.jit-ig-modal__close');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 100);
    }

    // Create Instagram embed blockquote
    const blockquote = document.createElement('blockquote');
    blockquote.className = 'instagram-media';
    blockquote.dataset.instgrmPermalink = postUrl;
    blockquote.dataset.instgrmVersion = '14';
    blockquote.style.cssText = 'background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 0 auto; max-width:540px; min-width:326px; padding:0; width:calc(100% - 2px);';
    
    // Add a simple loading placeholder inside
    blockquote.innerHTML = '<div style="padding:16px;"><a href="' + postUrl + '" target="_blank" rel="noopener noreferrer" style="color:#c9c8cd;font-family:Arial,sans-serif;font-size:14px;font-style:normal;font-weight:normal;line-height:17px;text-decoration:none;">View on Instagram</a></div>';
    
    embedContainer.appendChild(blockquote);

    // Load embed script and process
    loadEmbedScript()
      .then(() => {
        // Process the embed
        if (window.instgrm && window.instgrm.Embeds) {
          window.instgrm.Embeds.process();
        }

        // Set a timeout for fallback
        setTimeout(() => {
          // Check if embed was successful (iframe should exist)
          const iframe = embedContainer.querySelector('iframe');
          if (!iframe) {
            loadingEl.hidden = true;
            fallbackEl.hidden = false;
          } else {
            loadingEl.hidden = true;
          }
        }, 5000);

        // Also listen for iframe load
        const checkForIframe = setInterval(() => {
          const iframe = embedContainer.querySelector('iframe');
          if (iframe) {
            loadingEl.hidden = true;
            clearInterval(checkForIframe);
          }
        }, 500);

        // Clear after max time
        setTimeout(() => clearInterval(checkForIframe), 10000);
      })
      .catch(() => {
        loadingEl.hidden = true;
        fallbackEl.hidden = false;
      });

    // Trap focus in modal
    trapFocus(modal);
  }

  function closeModal(modal) {
    if (!modal) return;

    modal.hidden = true;
    document.body.style.overflow = '';

    // Return focus to trigger
    if (triggerElement) {
      triggerElement.focus();
      triggerElement = null;
    }

    activeModal = null;
  }

  function loadEmbedScript() {
    return new Promise((resolve, reject) => {
      if (embedScriptLoaded) {
        resolve();
        return;
      }

      if (embedScriptLoading) {
        // Wait for existing load
        const checkLoaded = setInterval(() => {
          if (embedScriptLoaded) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkLoaded);
          reject(new Error('Script load timeout'));
        }, 10000);
        
        return;
      }

      embedScriptLoading = true;

      const script = document.createElement('script');
      script.id = 'instagram-embed-script';
      script.src = 'https://www.instagram.com/embed.js';
      script.async = true;

      script.onload = () => {
        embedScriptLoaded = true;
        embedScriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        embedScriptLoading = false;
        reject(new Error('Failed to load Instagram embed script'));
      };

      document.body.appendChild(script);
    });
  }

  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    function handleTabKey(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    element.addEventListener('keydown', handleTabKey);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
